// 花嫁と亡霊の当たりの前端 = 描かれた絵の前端 + 余白 (2026-10-05 ユーザー「直す・比べて選ぶ」→ 比較版を遊んで「A 絵に合わせる」)。
//
// 問い: 花嫁と亡霊は攻撃の当たりが見た目より前まで届く (大きい所で約100px)。絵の外で当たる理不尽さにつながる。
// 既定の値は src/attacks.ts の定義に直接書いてある。ここは配布版の `?bossReach=old` で旧値へ戻す比較用 (騎士の knight_reach.ts と同じ形)。
// 採寸 = docs/research/2026-10-05-closed-bosses-measurements.md C-2 (実機の通常ループ・スプライト本体の不透明画素)。
//   Sonnet の測り直しも一致 = docs/research/2026-10-05-sonnet-capture-measure-trial.md。比較の記録 = docs/research/2026-10-05-bride-shade-reach-fit.md。
// 光の弧 (花嫁の裾乱れ・三重の返礼の veil_slash_arc) は「絵」に数えない: 弧は向きに関係なく体の周りを回り
//   (render.ts drawVeilSlashArc・段によっては丸ごと背中側)、どの段が前を通るかが当たりと合っていないため。
// range と approachDistancePx も前端と同じ値 (踏み込みの着地 = approachDistancePx - gapCloseBuffer が当たりの内側)。
//   後ろ側 (localFromPx) は変えていない (背面カバーは見切り・跨ぎの別の約束)。
// 絵を差し替えたら測り直して BRIDE_SHADE_ART_FRONT_PX と attacks.ts を更新する (tests/combat_sim/bride_shade_reach_fit.test.ts が見張る)。
import { ATTACKS } from './attacks'

export type BrideShadeReachVariant = 'art' | 'old'

// 主人公の当たりは幅30 (半幅15) だが、見えている体はそれより広い。絵が主人公の見えている体に触れた所で当たる
//   ように、当たりの半幅ぶんだけ前へ足す (騎士の 6px は細い穂先の縁取り分で、体へ届く掴み・薙ぎとは別)。
export const REACH_FIT_MARGIN_PX = 15

// 当たりが生きている間の絵の前端 (px・ボス中心から前方・C-2 の「絵の前端（全体）」)。段で揺れる物は大きい方。
export const BRIDE_SHADE_ART_FRONT_PX = {
  // 一閃・返しの一閃は 10-21 に当たりの絵を veil_open → veil_sweep_contact へ替えた (喪の翻しと同じ絵・同じ倍率)。
  //   旧 123 / 117 は veil_open の前端。?brideMotion=old で絵だけ前へ戻しても当たりはこの値のまま。
  veilSweep: 87,
  veilSweepLink: 87,
  veilSweepPerilous: 87,
  // 回る連撃は体の絵 (57〜69) でなく、届きを見せる前の半円の糸 (src/bride_thread_sweep.ts THREAD_REACH_PX) が「絵」(10-05 ユーザー「B エフェクトで射程を見せる」)。
  veilBlinkTriple: 135,
  veilFlurry: 135,       // 0〜6段
  veilFlurrySlam: 94,    // 8段 (叩きつけ)
  vowEmbrace: 64,
  brideExecution: 90,
  shadeSlash: 71,
  shadeThrust: 84,       // 頭より上の腕まで含めた全体。主人公の背丈の内だけなら 32
  shadeGrab: 69,         // 同上。背丈の内だけなら 51
} as const

export const BRIDE_SHADE_REACH_ATTACK_IDS = ['veilSweep', 'veilSweepLink', 'veilSweepPerilous', 'veilBlinkTriple', 'veilFlurry',
  'vowEmbrace', 'brideExecution', 'shadeSlash', 'shadeThrust', 'shadeGrab'] as const
const FLURRY_SLAM_STAGE = 8

// 2026-10-05 より前の値 (当たりの前端 = range = approachDistancePx)。裾乱れの叩きつけ (8段) だけ別。
const OLD_FRONT: Record<typeof BRIDE_SHADE_REACH_ATTACK_IDS[number], number> = {
  veilSweep: 150, veilSweepLink: 150, veilSweepPerilous: 150, veilBlinkTriple: 150, veilFlurry: 150,
  vowEmbrace: 170, brideExecution: 150, shadeSlash: 130, shadeThrust: 140, shadeGrab: 170,
}
const OLD_FLURRY_SLAM_FRONT = 115

type Snapshot = Record<string, { range: number; approach: number | undefined; front: number; stages?: (number | undefined)[] }>
let captured: Snapshot | null = null
function snapshot(): Snapshot {
  const out: Snapshot = {}
  for (const id of BRIDE_SHADE_REACH_ATTACK_IDS) {
    const a = ATTACKS[id]!
    out[id] = { range: a.range, approach: a.approachDistancePx, front: a.strike!.localToPx,
      stages: a.comboStages?.map(s => s.strike?.localToPx) }
  }
  return out
}

function apply(get: (id: typeof BRIDE_SHADE_REACH_ATTACK_IDS[number]) => Snapshot[string]): void {
  for (const id of BRIDE_SHADE_REACH_ATTACK_IDS) {
    const a = ATTACKS[id]!, v = get(id)
    a.range = v.range
    a.approachDistancePx = v.approach
    a.strike = { ...a.strike!, localToPx: v.front }
    a.comboStages?.forEach((s, i) => {
      const to = v.stages?.[i]
      if (s.strike && to !== undefined) s.strike = { ...s.strike, localToPx: to }
    })
  }
}

let current: BrideShadeReachVariant = 'art'
export function getBrideShadeReachVariant(): BrideShadeReachVariant { return current }
// テストと起動時 (applyBrideShadeReachFromUrl) から呼ぶ。'art' で attacks.ts に書いた既定値へ戻す。
export function setBrideShadeReachVariant(variant: BrideShadeReachVariant): void {
  if (variant === current) return
  if (!captured) captured = snapshot()
  if (variant === 'old') {
    apply(id => ({ range: OLD_FRONT[id], approach: OLD_FRONT[id], front: OLD_FRONT[id],
      stages: captured![id]!.stages?.map((to, i) => to === undefined ? undefined : i === FLURRY_SLAM_STAGE ? OLD_FLURRY_SLAM_FRONT : OLD_FRONT[id]) }))
  } else {
    apply(id => captured![id]!)
  }
  current = variant
}

// 配布版でも読む (遊び比べ用)。読む URL のキーは bossReach だけ (scripts/check_dev_leak.mjs PUBLIC_URL_QUERY_KEYS)。
export function applyBrideShadeReachFromUrl(): void {
  if (typeof window === 'undefined') return
  if (new URLSearchParams(window.location.search).get('bossReach') === 'old') setBrideShadeReachVariant('old')
}
